import { BodyEmailTemplate, UserEmailTemplates } from '@numeo/types-core'

/**
 * Type definitions for updater lite communication
 */
interface UpdaterLiteUser {
    token: string
    userId?: string
    email?: string
    name?: string
    emailTemplates?: UserEmailTemplates | { bodyTemplates: BodyEmailTemplate[] | []; footer?: string }
    nylasEmails?: Array<{
        id: number | string
        email: string
        isPrimary: boolean
    }>
    integrations?: Record<string, unknown>
}

interface UpdaterLiteAuthResponse {
    status: string
    message?: string
    isExistingUser?: boolean
    error?: string
}

// Chrome runtime error handling helper
function getChromeRuntimeError(): Error | null {
    if (window.chrome?.runtime && 'lastError' in window.chrome.runtime && window.chrome.runtime.lastError) {
        const error = new Error(window.chrome.runtime.lastError.message || 'Chrome runtime error')
        return error
    }
    return null
}

/**
 * Service to handle communication with the Numeo updater lite
 */
class UpdaterLiteAuthService {
    private updaterLiteId: string | null = null
    private connected = false
    private readonly storageKey = 'updaterLiteId'

    constructor() {
        this.updaterLiteId = localStorage.getItem(this.storageKey) || localStorage.getItem('updaterLiteId') || sessionStorage.getItem('updaterLiteId')
    }

    /**
     * Initialize connection with the updater lite
     * @returns Promise that resolves when connection is established or rejects if updater lite is not found
     */
    async connect(): Promise<boolean> {
        if (!this.updaterLiteId) {
            return false
        }

        if (!window.chrome?.runtime) {
            return false
        }

        try {
            const response = await this.sendIdentityCheck()
            this.connected = response && response.status === 'success'

            if (!this.connected) {
                localStorage.removeItem(this.storageKey)
                this.updaterLiteId = null
            }

            return this.connected
        } catch (error) {
            console.error('Error connecting to updater lite:', error)
            localStorage.removeItem(this.storageKey)
            this.updaterLiteId = null
            this.connected = false
            return false
        }
    }

    /**
     * Send an identity check message to the updater lite
     */
    private sendIdentityCheck(): Promise<UpdaterLiteAuthResponse> {
        return new Promise((resolve, reject) => {
            if (!this.updaterLiteId || !window.chrome?.runtime) {
                reject(new Error('Updater lite ID not available or Chrome runtime not found'))
                return
            }

            try {
                window.chrome.runtime.sendMessage(
                    this.updaterLiteId,
                    { type: 'UPDATER_LITE_ID_CHECK' } as Record<string, unknown>,
                    (response: Record<string, unknown>) => {
                        const runtimeError = getChromeRuntimeError()
                        if (runtimeError) {
                            reject(new Error(runtimeError.message || 'Unknown error'))
                            return
                        }
                        resolve({
                            status: response.status as string,
                            message: response.message as string | undefined,
                            isExistingUser: response.isExistingUser as boolean | undefined,
                            error: response.error as string | undefined,
                        })
                    }
                )
            } catch (error) {
                reject(error)
            }
        })
    }

    /**
     * Send authentication data to the updater lite
     * @param user User data to send
     * @returns Promise that resolves with the updater lite's response
     */
    async syncAuthState(user: UpdaterLiteUser | null): Promise<UpdaterLiteAuthResponse | null> {
        if (!this.updaterLiteId) {
            this.updaterLiteId = localStorage.getItem(this.storageKey) || localStorage.getItem('updaterLiteId') || sessionStorage.getItem('updaterLiteId')
        }

        if (!this.updaterLiteId) {
            console.error('Updater lite ID not available anywhere')
            return {
                status: 'error',
                message: 'Updater lite ID not found. Please try again from the updater lite.',
            }
        }

        if (!this.connected) {
            const connected = await this.connect()
            if (!connected) {
                return {
                    status: 'error',
                    message: 'Could not connect to updater lite.',
                }
            }
        }

        try {
            if (user) {
                localStorage.setItem('numeo-updater-lite-connected', 'true')
                return await this.sendAuthMessage(user)
            } else {
                localStorage.removeItem('numeo-updater-lite-connected')
                return await this.sendLogoutMessage()
            }
        } catch (error) {
            console.error('Error syncing auth state:', error)
            this.connected = false
            return {
                status: 'error',
                message: 'Communication failed. Please try again.',
            }
        }
    }

    /**
     * Send authentication message to the updater lite
     */
    private sendAuthMessage(user: UpdaterLiteUser): Promise<UpdaterLiteAuthResponse> {
        return new Promise((resolve, reject) => {
            if (!this.updaterLiteId || !window.chrome?.runtime) {
                reject(new Error('Updater lite ID not available or Chrome runtime not found'))
                return
            }

            try {
                window.chrome.runtime.sendMessage(
                    this.updaterLiteId,
                    {
                        type: 'DAT_AUTH_SUCCESS',
                        user: user,
                        originalTabId: 0, // Default tab ID as it's required by the message format
                    } as Record<string, unknown>,
                    (response: Record<string, unknown>) => {
                        const runtimeError = getChromeRuntimeError()
                        if (runtimeError) {
                            reject(new Error(runtimeError.message || 'Unknown error'))
                            return
                        }
                        resolve({
                            status: response.status as string,
                            message: response.message as string | undefined,
                            isExistingUser: response.isExistingUser as boolean | undefined,
                            error: response.error as string | undefined,
                        })
                    }
                )
            } catch (error) {
                reject(error)
            }
        })
    }

    /**
     * Send logout message to the updater lite
     */
    private sendLogoutMessage(): Promise<UpdaterLiteAuthResponse> {
        return new Promise((resolve, reject) => {
            if (!this.updaterLiteId || !window.chrome?.runtime) {
                reject(new Error('Updater lite ID not available or Chrome runtime not found'))
                return
            }

            try {
                window.chrome.runtime.sendMessage(
                    this.updaterLiteId,
                    {
                        type: 'DAT_AUTH_LOGOUT',
                        action: 'logout',
                    } as Record<string, unknown>,
                    (response: Record<string, unknown>) => {
                        const runtimeError = getChromeRuntimeError()
                        if (runtimeError) {
                            reject(new Error(runtimeError.message || 'Unknown error'))
                            return
                        }
                        resolve({
                            status: response.status as string,
                            message: response.message as string | undefined,
                            isExistingUser: response.isExistingUser as boolean | undefined,
                            error: response.error as string | undefined,
                        })
                    }
                )
            } catch (error) {
                reject(error)
            }
        })
    }

    /**
     * Store the updater lite ID for future use
     * @param updaterLiteId Updater lite ID to store
     */
    setUpdaterLiteId(updaterLiteId: string): void {
        this.updaterLiteId = updaterLiteId

        localStorage.setItem(this.storageKey, updaterLiteId)
        localStorage.setItem('updaterLiteId', updaterLiteId)
        sessionStorage.setItem('updaterLiteId', updaterLiteId)
    }

    /**
     * Check if we're connected to the updater lite
     */
    isConnected(): boolean {
        return this.connected
    }

    /**
     * Get the stored updater lite ID
     */
    getUpdaterLiteId(): string | null {
        return this.updaterLiteId
    }
}

// Export as singleton
export const updaterLiteAuthService = new UpdaterLiteAuthService()
